import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { OfflineProvider } from "./contexts/OfflineContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { ImpersonationProvider, useImpersonation } from "./contexts/ImpersonationContext";
import IdleTimeout from "./components/IdleTimeout";
import { PerformanceBadge } from "./components/PerformanceBadge";
import UATLauncher from "./components/UATLauncher";
import ImpersonationBanner from "./components/ImpersonationBanner";
import MaintenanceBanner from "./components/MaintenanceBanner";

// Pages
import ErrorBoundary from "./components/ErrorBoundary";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import HomePage from "./pages/HomePage";
import SchedulePage from "./pages/SchedulePage";
import BatchEntryPage from "./pages/BatchEntryPage";
import CalendarPage from "./pages/CalendarPage";
import CustomersPage from "./pages/CustomersPage";
import ClientSubRegionsPage from "./pages/ClientSubRegionsPage";
import ClientDashboardLayoutsPage from "./pages/ClientDashboardLayoutsPage";
import ServicesPage from "./pages/ServicesPage";
import SettingsPage from "./pages/SettingsPage";
import ReportsPage from "./pages/ReportsPage";
import UsersPage from "./pages/UsersPage";
import ActivityLogPage from "./pages/ActivityLogPage";
import HolidaysPage from "./pages/HolidaysPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import PrivacyPage from "./pages/PrivacyPage";
import TrademarkPage from "./pages/TrademarkPage";
import ContactPage from "./pages/ContactPage";
import NotificationLogsPage from "./pages/NotificationLogsPage";
import JobDetailPage from "./pages/JobDetailPage";
import ReportsRepositoryPage from "./pages/ReportsRepositoryPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import Layout from "./pages/Layout";
import SummaryStockAuditReportPage from "./pages/SummaryStockAuditReportPage";
import SummaryReportViewPage from "./pages/SummaryReportViewPage";
import ActionPlanDetailPage from "./pages/ActionPlanDetailPage";
import ActionPlanListPage from "./pages/ActionPlanListPage";
import ActionPlanReportPage from "./pages/ActionPlanReportPage";
import ClientActionPlanPage from "./pages/ClientActionPlanPage";
import ClientAPWorklist from "./pages/ClientAPWorklist";
import ClientAPAnalytics from "./pages/ClientAPAnalytics";
import AuditFrequencyCompliancePage from "./pages/AuditFrequencyCompliancePage";
import ForecastVarianceReportPage from "./pages/ForecastVarianceReportPage"; // iter447
import RevenueBudgetReportPage from "./pages/RevenueBudgetReportPage"; // iter478
import PerformanceCentrePage from "./pages/PerformanceCentrePage"; // Performance Centre v1
import ManagementDigestPage from "./pages/ManagementDigestPage"; // Scheduled Insights
import CustomerSiteDirectoryPage from "./pages/CustomerSiteDirectoryPage";
import ReportCompletenessPage from "./pages/ReportCompletenessPage";
import InvoiceListPage from "./pages/InvoiceListPage";
import SalesInvoicingPage from "./pages/SalesInvoicingPage";
import InviteAcceptPage from "./pages/InviteAcceptPage";
import InviteActivatePage from "./pages/InviteActivatePage";
import MaintenancePage from "./pages/MaintenancePage";
import IncentivesPage from "./pages/IncentivesPage";
import IncentiveRunPage from "./pages/IncentiveRunPage";
import IncentiveSettingsPage from "./pages/IncentiveSettingsPage";
import IncentiveSchemesPage from "./pages/IncentiveSchemesPage";
import IncentiveSchemeEditorPage from "./pages/IncentiveSchemeEditorPage";
import IncentiveTypesPage from "./pages/IncentiveTypesPage";
import BeverageStockMasterPage from "./pages/BeverageStockMasterPage";
import AppsPage from "./pages/AppsPage";

// Stocktaking Pages (Phase 3)
import SuppliersPage from "./pages/SuppliersPage";
import SectionMasterDataPage from "./pages/SectionMasterDataPage";
import DeliveriesRegisterPage from "./pages/DeliveriesRegisterPage";
import DeliveryEntryPage from "./pages/DeliveryEntryPage";
import CreditsRegisterPage from "./pages/CreditsRegisterPage";
import CreditEntryPage from "./pages/CreditEntryPage";
import SalesRegisterPage from "./pages/SalesRegisterPage";
import SalesEntryPage from "./pages/SalesEntryPage";
import StocktakingDashboardPage from "./pages/StocktakingDashboardPage";
import ImportStocktakeLandingPage from "./pages/ImportStocktakeLandingPage";
import StartNewStocktakePage from "./pages/StartNewStocktakePage";
import ImportStocktakeComingSoonPage from "./pages/ImportStocktakeComingSoonPage";
import AddHistoricalStocktakePage from "./pages/AddHistoricalStocktakePage";
import LegacyClosingSnapshotPage from "./pages/LegacyClosingSnapshotPage";
import PsbPipelineProgressPage from "./pages/PsbPipelineProgressPage";
import StocktakeSettingsPage from "./pages/StocktakeSettingsPage";
import SiteBarPricesPage from "./pages/SiteBarPricesPage";
import StocktakingOrgSettingsPage from "./pages/StocktakingOrgSettingsPage";
import SiteInventoryPage from "./pages/SiteInventoryPage";
import MeasureDefinitionsPage from "./pages/MeasureDefinitionsPage";
import SiteLocationsPage from "./pages/SiteLocationsPage";
import SiteForwardSheetsPage from "./pages/SiteForwardSheetsPage";
import StocktakeSessionsPage from "./pages/StocktakeSessionsPage";
import StocktakeSessionPage from "./pages/StocktakeSessionPage";
import StocktakeHistoryPage from "./pages/StocktakeHistoryPage";
import StocktakeCostingReportPage from "./pages/StocktakeCostingReportPage";
import StocktakeReportsPage from "./pages/StocktakeReportsPage";
import RecipesAdminPage from "./pages/RecipesAdminPage";
import AdminEmailDiagnosticsPage from "./pages/AdminEmailDiagnosticsPage";
import BBUATFixturePage from "./pages/BBUATFixturePage";
import OfflineSessionsPage from "./pages/OfflineSessionsPage";
import ProductCategoriesAdminPage from "./pages/ProductCategoriesAdminPage";
import ProductMasterAuditPage from "./pages/ProductMasterAuditPage";
import PsbStage1Page from "./pages/PsbStage1Page";
import PsbStage2Page from "./pages/PsbStage2Page";
import PsbStage3Page from "./pages/PsbStage3Page";
import PsbStage4Page from "./pages/PsbStage4Page";
import PsbStage5Page from "./pages/PsbStage5Page";
import PsbStage6aPage from "./pages/PsbStage6aPage";
import PsbStage6bPage from "./pages/PsbStage6bPage";
import PsbStage6cPage from "./pages/PsbStage6cPage";
import PsbStage1BPage from "./pages/PsbStage1BPage";
import PsbFixturePage from "./pages/PsbFixturePage";
import PsbStage7Page from "./pages/PsbStage7Page";
import PsbHistoricalConversionWizardPage from "./pages/PsbHistoricalConversionWizardPage";
import PsbAliasRegistryPage from "./pages/PsbAliasRegistryPage";
import BulkMeasureAssignPage from "./pages/BulkMeasureAssignPage";
import CopyAssignedProductsPage from "./pages/CopyAssignedProductsPage";

// Count Mode Pages (Phase A)
import CountLayout from "./pages/count/CountLayout";
import CountSessionPickerPage from "./pages/count/CountSessionPickerPage";
import CountSessionPage from "./pages/count/CountSessionPage";
import CountSyncPage from "./pages/count/CountSyncPage";
import CountObservationsPage from "./pages/count/CountObservationsPage";
import ReconciliationPage from "./pages/count/ReconciliationPage";
import CountTransferPage from "./pages/count/CountTransferPage";
import ExceptionQueuePage from "./pages/ExceptionQueuePage";

// Client Portal Pages (Phase 2.2A/2.3A/2.3B)
import ClientPortalLayout from "./pages/ClientPortalLayout";
import ClientDashboardPage from "./pages/ClientDashboardPage";
import ClientReportsPage from "./pages/ClientReportsPage";
import ClientUpcomingAuditsPage from "./pages/ClientUpcomingAuditsPage";
import ClientPropertyDashboardPage from "./pages/ClientPropertyDashboardPage";
import ClientReportViewPage from "./pages/ClientReportViewPage";
import ClientContactPage from "./pages/ClientContactPage";

// Protected Route Wrapper for INTERNAL users
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, mustChangePassword, user } = useAuth();
  const { isClientView, loading: impersonationLoading } = useImpersonation();

  if (loading || impersonationLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Force password change if required
  if (mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  // Redirect CLIENT users to client portal
  if (user?.user_type === 'CLIENT') {
    return <Navigate to="/client/dashboard" replace />;
  }

  // Ring-fence: if admin is impersonating a CLIENT, block access to internal routes
  // and force them into the client portal until they click "Exit View".
  // Auditor-view sessions stay in the full internal app — do NOT ring-fence them.
  if (isClientView) {
    return <Navigate to="/client/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Admin-only route: redirects non-Admins to /home
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, isAdmin } = useAuth();
  const { isAuditorView, loading: impersonationLoading } = useImpersonation();
  if (loading || impersonationLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // During auditor-view the frontend auth context reflects the auditor's role
  // (via refreshUser). Belt-and-suspenders: block admin-only routes explicitly
  // even during any brief window before that refresh commits.
  if (!isAdmin || isAuditorView) return <Navigate to="/home" replace />;
  return <Layout>{children}</Layout>;
};

// StocktakeUser route: any internal Admin or Auditor can access PSB import and
// historical-conversion pages. The isAuditorView flag is deliberately NOT checked
// here: when an Admin uses "View as Auditor", refreshUser() sets isAdmin=false and
// isAuditor=true so the simulation correctly reflects what a real auditor sees.
// AdminRoute still blocks isAuditorView for genuinely admin-only pages.
const StocktakeUserRoute = ({ children }) => {
  const { isAuthenticated, loading, isAdmin, isAuditor } = useAuth();
  const { loading: impersonationLoading } = useImpersonation();
  if (loading || impersonationLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isAdmin || isAuditor) return <Layout>{children}</Layout>;
  return <Navigate to="/home" replace />;
};

// Admin or Senior Scheduler route: all other roles are redirected to /home
const SeniorSchedulerOrAdminRoute = ({ children }) => {
  const { isAuthenticated, loading, isAdmin, isSeniorScheduler } = useAuth();
  const { isAuditorView, loading: impersonationLoading } = useImpersonation();
  if (loading || impersonationLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Block elevated routes during auditor-view even before auth-context refresh commits.
  if ((!isAdmin && !isSeniorScheduler) || isAuditorView) return <Navigate to="/home" replace />;
  return <Layout>{children}</Layout>;
};

// Protected Route Wrapper for CLIENT users (Client Portal)
const ClientProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, mustChangePassword, user } = useAuth();
  const { isClientView, loading: impersonationLoading } = useImpersonation();

  if (loading || impersonationLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Force password change if required
  if (mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  // Allow INTERNAL users with an active CLIENT impersonation session to access the
  // client portal. Auditor-view sessions must NOT enter the client portal.
  if (user?.user_type !== 'CLIENT' && !isClientView) {
    return <Navigate to="/home" replace />;
  }

  return <ClientPortalLayout>{children}</ClientPortalLayout>;
};

// Public Route Wrapper (redirects to appropriate home based on user type)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Route to appropriate home based on user type
    if (user?.user_type === 'CLIENT') {
      return <Navigate to="/client/dashboard" replace />;
    }
    return <Navigate to="/home" replace />;
  }

  return children;
};

// Protected Route Wrapper for COUNT MODE (tablet-optimised)
const CountProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, mustChangePassword, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-cyan-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  // Role gate: auditor, admin, scheduler, senior_scheduler
  const userRoles = (user?.roles || []).map(r => r.toLowerCase());
  const legacyRole = (user?.role || '').toLowerCase();
  const allRoles = new Set([...userRoles, legacyRole].filter(Boolean));
  const hasCountAccess = ['auditor', 'admin', 'scheduler', 'senior_scheduler'].some(r => allRoles.has(r));

  if (!hasCountAccess) {
    return <Navigate to="/home" replace />;
  }

  return <CountLayout>{children}</CountLayout>;
};

// ─── Count PWA Isolation ───
// When installed from count-manifest.json, the start_url includes ?app=count.
// On first launch we persist a localStorage flag. From then on, ALL navigation
// outside /count is redirected back to /count/sessions, keeping the PWA locked.

const COUNT_PWA_FLAG = 'az_count_pwa';

function bootCountPWAFlag() {
  // Already flagged from a previous launch
  if (localStorage.getItem(COUNT_PWA_FLAG) === '1') return;
  // First launch: check for the manifest marker in the URL
  const params = new URLSearchParams(window.location.search);
  if (params.get('app') === 'count') {
    localStorage.setItem(COUNT_PWA_FLAG, '1');
    // Strip the marker from the URL so it doesn't linger
    params.delete('app');
    const clean = params.toString();
    const newUrl = window.location.pathname + (clean ? `?${clean}` : '') + window.location.hash;
    window.history.replaceState(null, '', newUrl);
  }
  // Fallback: standalone mode + /count path
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  if (isStandalone && window.location.pathname.startsWith('/count')) {
    localStorage.setItem(COUNT_PWA_FLAG, '1');
  }
}

// Run once at module load (before React renders)
bootCountPWAFlag();

function isCountPWA() {
  return localStorage.getItem(COUNT_PWA_FLAG) === '1';
}

function CountPWAGuard({ children }) {
  const { pathname } = useLocation();
  // Only lock navigation when BOTH: the flag is set AND the app is running
  // in standalone (installed PWA) mode. Normal browser tabs stay free.
  const isStandalone = typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true);
  if (isCountPWA() && isStandalone && !pathname.startsWith('/count') && pathname !== '/login' && pathname !== '/change-password') {
    return <Navigate to="/count" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <CountPWAGuard>
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />

      {/* Invitation acceptance — public (iter311) */}
      <Route path="/invite/:token" element={<InviteAcceptPage />} />
      <Route path="/activate" element={<InviteActivatePage />} />
      
      {/* Password Reset Routes - Public */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Change Password - Accessible when logged in but must change password */}
      <Route
        path="/change-password"
        element={<ChangePasswordPage />}
      />

      {/* Protected Routes */}
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule"
        element={
          <ProtectedRoute>
            <SchedulePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule/batch"
        element={
          <ProtectedRoute>
            <BatchEntryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications/logs"
        element={
          <ProtectedRoute>
            <NotificationLogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <CustomersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/:clientId/sub-regions"
        element={
          <ProtectedRoute>
            <ClientSubRegionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/services"
        element={
          <ProtectedRoute>
            <ServicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/beverage-stock-master"
        element={
          <ProtectedRoute>
            <BeverageStockMasterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/client-portal/dashboard-layouts"
        element={
          <ProtectedRoute>
            <ClientDashboardLayoutsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity"
        element={
          <ProtectedRoute>
            <ActivityLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holidays"
        element={
          <ProtectedRoute>
            <HolidaysPage />
          </ProtectedRoute>
        }
      />

      {/* Legacy redirects */}
      <Route path="/sites" element={<Navigate to="/customers" replace />} />
      <Route path="/clients" element={<Navigate to="/customers" replace />} />

      {/* Footer pages - Public */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/trademark" element={<TrademarkPage />} />
      <Route path="/contact" element={<ContactPage />} />

      {/* Job Detail Route */}
      <Route
        path="/jobs/:id"
        element={
          <ProtectedRoute>
            <JobDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Summary Stock Audit Report - Phase 2A */}
      <Route
        path="/jobs/:jobId/summary-report"
        element={
          <ProtectedRoute>
            <SummaryStockAuditReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/summary-reports/:reportId"
        element={
          <ProtectedRoute>
            <SummaryStockAuditReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/summary-reports/:reportId/view"
        element={
          <ProtectedRoute>
            <SummaryReportViewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/summary-reports/:reportId/action-plan"
        element={
          <ProtectedRoute>
            <ActionPlanDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/action-plans"
        element={
          <ProtectedRoute>
            <ActionPlanListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/action-plans/report"
        element={
          <ProtectedRoute>
            <ActionPlanReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/action-plans/:planId"
        element={
          <ProtectedRoute>
            <ActionPlanDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Reports Repository - Phase 2.6A */}
      <Route
        path="/reports-repository"
        element={
          <ProtectedRoute>
            <ReportsRepositoryPage />
          </ProtectedRoute>
        }
      />

      {/* Audit Frequency Compliance Report */}
      <Route
        path="/reports/audit-frequency-compliance"
        element={
          <ProtectedRoute>
            <AuditFrequencyCompliancePage />
          </ProtectedRoute>
        }
      />

      {/* iter447 — Forecast Revenue Variance Report */}
      <Route
        path="/reports/forecast-variance"
        element={
          <ProtectedRoute>
            <ForecastVarianceReportPage />
          </ProtectedRoute>
        }
      />

      {/* iter478 — Revenue Budget Report */}
      <Route
        path="/reports/revenue-budget"
        element={
          <ProtectedRoute>
            <RevenueBudgetReportPage />
          </ProtectedRoute>
        }
      />

      {/* Performance Centre v1 */}
      <Route
        path="/performance"
        element={
          <ProtectedRoute>
            <PerformanceCentrePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/management-digest"
        element={
          <ProtectedRoute>
            <ManagementDigestPage />
          </ProtectedRoute>
        }
      />

      {/* Customer & Site Directory Report */}
      <Route
        path="/reports/customer-site-directory"
        element={
          <ProtectedRoute>
            <CustomerSiteDirectoryPage />
          </ProtectedRoute>
        }
      />

      {/* Report Completeness Audit */}
      <Route
        path="/reports/completeness"
        element={
          <ProtectedRoute>
            <ReportCompletenessPage />
          </ProtectedRoute>
        }
      />

      {/* Invoice List Report */}
      <Route
        path="/reports/invoice-list"
        element={
          <SeniorSchedulerOrAdminRoute>
            <InvoiceListPage />
          </SeniorSchedulerOrAdminRoute>
        }
      />

      {/* Sales Invoicing workspace — Admin / Senior Scheduler only */}
      <Route
        path="/sales-invoicing"
        element={
          <SeniorSchedulerOrAdminRoute>
            <SalesInvoicingPage />
          </SeniorSchedulerOrAdminRoute>
        }
      />

      {/* System Maintenance - Admin Only */}
      <Route
        path="/maintenance"
        element={
          <AdminRoute>
            <MaintenancePage />
          </AdminRoute>
        }
      />

      {/* Client Portal Routes - Phase 2.2A/2.3A/2.3B */}
      <Route
        path="/client/dashboard"
        element={
          <ClientProtectedRoute>
            <ClientDashboardPage />
          </ClientProtectedRoute>
        }
      />
      <Route
        path="/client/reports"
        element={
          <ClientProtectedRoute>
            <ClientReportsPage />
          </ClientProtectedRoute>
        }
      />
      <Route
        path="/client/audits"
        element={
          <ClientProtectedRoute>
            <ClientUpcomingAuditsPage />
          </ClientProtectedRoute>
        }
      />
      <Route
        path="/client/property-dashboard"
        element={
          <ClientProtectedRoute>
            <ClientPropertyDashboardPage />
          </ClientProtectedRoute>
        }
      />
      <Route
        path="/client/report/:reportId"
        element={
          <ClientProtectedRoute>
            <ClientReportViewPage />
          </ClientProtectedRoute>
        }
      />
      <Route
        path="/client/contact"
        element={
          <ClientProtectedRoute>
            <ClientContactPage />
          </ClientProtectedRoute>
        }
      />
      <Route
        path="/client/privacy"
        element={
          <ClientProtectedRoute>
            <PrivacyPage />
          </ClientProtectedRoute>
        }
      />
      <Route
        path="/client/trademark"
        element={
          <ClientProtectedRoute>
            <TrademarkPage />
          </ClientProtectedRoute>
        }
      />
      <Route path="/client" element={<Navigate to="/client/dashboard" replace />} />
      <Route
        path="/client/action-plans/worklist"
        element={
          <ClientProtectedRoute>
            <ClientAPWorklist />
          </ClientProtectedRoute>
        }
      />
      <Route
        path="/client/action-plans/analytics"
        element={
          <ClientProtectedRoute>
            <ClientAPAnalytics />
          </ClientProtectedRoute>
        }
      />
      <Route
        path="/client/action-plans"
        element={
          <ClientProtectedRoute>
            <ClientActionPlanPage />
          </ClientProtectedRoute>
        }
      />
      <Route
        path="/client/action-plans/:planId"
        element={
          <ClientProtectedRoute>
            <ClientActionPlanPage />
          </ClientProtectedRoute>
        }
      />

      {/* Default Redirect */}
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="/dashboard" element={<Navigate to="/home" replace />} />
      <Route path="/jobs" element={<Navigate to="/schedule" replace />} />

      {/* Stocktaking Module - Phase 3 */}
      <Route path="/stocktaking/dashboard" element={<ProtectedRoute><StocktakingDashboardPage /></ProtectedRoute>} />
      <Route path="/stocktaking/import" element={<ProtectedRoute><ImportStocktakeLandingPage /></ProtectedRoute>} />
      <Route path="/stocktaking/import/new-stocktake" element={<ProtectedRoute><StartNewStocktakePage /></ProtectedRoute>} />
      <Route path="/stocktaking/import/historical-stocktake" element={<StocktakeUserRoute><AddHistoricalStocktakePage /></StocktakeUserRoute>} />
      <Route path="/stocktaking/import/closing-stock" element={<StocktakeUserRoute><AddHistoricalStocktakePage sourceFirst /></StocktakeUserRoute>} />
      <Route path="/stocktaking/import/closing-snapshot" element={<StocktakeUserRoute><LegacyClosingSnapshotPage /></StocktakeUserRoute>} />
      <Route path="/stocktaking/import/psb-progress" element={<StocktakeUserRoute><PsbPipelineProgressPage /></StocktakeUserRoute>} />
      <Route path="/stocktaking/import/:choice" element={<ProtectedRoute><ImportStocktakeComingSoonPage /></ProtectedRoute>} />
      <Route path="/stocktaking/suppliers" element={<ProtectedRoute><SuppliersPage /></ProtectedRoute>} />
      <Route path="/stocktaking/allowances-master" element={<ProtectedRoute><SectionMasterDataPage category="allowance" title="Allowances" description="Standard allowance names used in Summary Audit Reports" /></ProtectedRoute>} />
      <Route path="/stocktaking/management-master" element={<ProtectedRoute><SectionMasterDataPage category="management" title="Management Items" description="Standard management item names used in Summary Audit Reports" /></ProtectedRoute>} />
      <Route path="/stocktaking/adjustments-master" element={<ProtectedRoute><SectionMasterDataPage category="adjustment" title="Adjustments" description="Standard adjustment names used in Summary Audit Reports" /></ProtectedRoute>} />
      <Route path="/stocktaking/deliveries" element={<ProtectedRoute><DeliveriesRegisterPage /></ProtectedRoute>} />
      <Route path="/stocktaking/deliveries/new" element={<ProtectedRoute><DeliveryEntryPage /></ProtectedRoute>} />
      <Route path="/stocktaking/deliveries/:id" element={<ProtectedRoute><DeliveryEntryPage /></ProtectedRoute>} />
      <Route path="/stocktaking/credits" element={<ProtectedRoute><CreditsRegisterPage /></ProtectedRoute>} />
      <Route path="/stocktaking/credits/new" element={<ProtectedRoute><CreditEntryPage /></ProtectedRoute>} />
      <Route path="/stocktaking/credits/:id" element={<ProtectedRoute><CreditEntryPage /></ProtectedRoute>} />
      <Route path="/stocktaking/sales" element={<ProtectedRoute><SalesRegisterPage /></ProtectedRoute>} />
      <Route path="/stocktaking/sales/new" element={<ProtectedRoute><SalesEntryPage /></ProtectedRoute>} />
      <Route path="/stocktaking/sales/:id" element={<ProtectedRoute><SalesEntryPage /></ProtectedRoute>} />
      <Route path="/stocktaking/delivery-register" element={<ProtectedRoute><DeliveriesRegisterPage /></ProtectedRoute>} />
      <Route path="/stocktaking/credit-register" element={<ProtectedRoute><CreditsRegisterPage /></ProtectedRoute>} />
      <Route path="/stocktaking/site-settings" element={<ProtectedRoute><StocktakeSettingsPage /></ProtectedRoute>} />
      <Route path="/stocktaking/site-bar-prices" element={<ProtectedRoute><SiteBarPricesPage /></ProtectedRoute>} />
      <Route path="/stocktaking/org-settings" element={<ProtectedRoute><StocktakingOrgSettingsPage /></ProtectedRoute>} />
      <Route path="/stocktaking/site-inventory" element={<ProtectedRoute><SiteInventoryPage /></ProtectedRoute>} />
      <Route path="/stocktaking/measures" element={<ProtectedRoute><MeasureDefinitionsPage /></ProtectedRoute>} />
      <Route path="/stocktaking/locations" element={<ProtectedRoute><SiteLocationsPage /></ProtectedRoute>} />
      <Route path="/stocktaking/forward-sheets" element={<ProtectedRoute><SiteForwardSheetsPage /></ProtectedRoute>} />
      <Route path="/stocktaking/sessions" element={<ProtectedRoute><StocktakeSessionsPage /></ProtectedRoute>} />
      <Route path="/stocktaking/sessions/:id" element={<ProtectedRoute><StocktakeSessionPage /></ProtectedRoute>} />
      <Route path="/stocktaking/offline" element={<ProtectedRoute><OfflineSessionsPage /></ProtectedRoute>} />
      <Route path="/stocktaking/session-history" element={<ProtectedRoute><StocktakeHistoryPage /></ProtectedRoute>} />
      <Route path="/stocktaking/costing-report" element={<ProtectedRoute><StocktakeCostingReportPage /></ProtectedRoute>} />
      <Route path="/stocktaking/reports" element={<ProtectedRoute><StocktakeReportsPage /></ProtectedRoute>} />
      <Route path="/settings/recipes" element={<ProtectedRoute><RecipesAdminPage /></ProtectedRoute>} />
      <Route path="/settings/email-diagnostics" element={<ProtectedRoute><AdminEmailDiagnosticsPage /></ProtectedRoute>} />
      <Route path="/dev/uat/bb-fixture" element={<AdminRoute><BBUATFixturePage /></AdminRoute>} />
      <Route path="/stocktaking/product-categories" element={<ProtectedRoute><ProductCategoriesAdminPage /></ProtectedRoute>} />
      <Route path="/stocktaking/product-master-audit" element={<AdminRoute><ProductMasterAuditPage /></AdminRoute>} />
      {/* PSB Historical Conversion — primary user journey */}
      <Route path="/stocktaking/historical-conversion" element={<StocktakeUserRoute><PsbHistoricalConversionWizardPage /></StocktakeUserRoute>} />
      {/* PSB Diagnostic / Recovery stages — StocktakeUser (Admin + Auditor), not in ordinary nav */}
      <Route path="/stocktaking/psb-stage1-import" element={<StocktakeUserRoute><PsbStage1Page /></StocktakeUserRoute>} />
      <Route path="/stocktaking/psb-stage2-import" element={<StocktakeUserRoute><PsbStage2Page /></StocktakeUserRoute>} />
      <Route path="/stocktaking/psb-stage3-import" element={<StocktakeUserRoute><PsbStage3Page /></StocktakeUserRoute>} />
          <Route path="/stocktaking/psb-stage4-import" element={<StocktakeUserRoute><PsbStage4Page /></StocktakeUserRoute>} />
          <Route path="/stocktaking/psb-stage5-import" element={<StocktakeUserRoute><PsbStage5Page /></StocktakeUserRoute>} />
          <Route path="/stocktaking/psb-stage6a-import" element={<StocktakeUserRoute><PsbStage6aPage /></StocktakeUserRoute>} />
          <Route path="/stocktaking/psb-stage6b-import" element={<StocktakeUserRoute><PsbStage6bPage /></StocktakeUserRoute>} />
          <Route path="/stocktaking/psb-stage6c-import" element={<StocktakeUserRoute><PsbStage6cPage /></StocktakeUserRoute>} />
          <Route path="/stocktaking/psb-stage1b-mapping" element={<StocktakeUserRoute><PsbStage1BPage /></StocktakeUserRoute>} />
          <Route path="/stocktaking/psb-stage1b-fixture" element={<StocktakeUserRoute><PsbFixturePage /></StocktakeUserRoute>} />
          <Route path="/stocktaking/psb-stage1b-alias-registry" element={<StocktakeUserRoute><PsbAliasRegistryPage /></StocktakeUserRoute>} />
          <Route path="/stocktaking/psb-stage7-calc" element={<StocktakeUserRoute><PsbStage7Page /></StocktakeUserRoute>} />
      <Route path="/stocktaking/bulk-measure-assign" element={<AdminRoute><BulkMeasureAssignPage /></AdminRoute>} />
      <Route path="/stocktaking/copy-assigned-products" element={<AdminRoute><CopyAssignedProductsPage /></AdminRoute>} />

      {/* Incentives Module */}
      <Route path="/incentives" element={<ProtectedRoute><IncentivesPage /></ProtectedRoute>} />
      <Route path="/incentives/types" element={<ProtectedRoute><IncentiveTypesPage /></ProtectedRoute>} />
      <Route path="/incentives/settings" element={<ProtectedRoute><IncentiveSettingsPage /></ProtectedRoute>} />
      <Route path="/incentives/schemes" element={<ProtectedRoute><IncentiveSchemesPage /></ProtectedRoute>} />
      <Route path="/incentives/schemes/new" element={<ProtectedRoute><IncentiveSchemeEditorPage /></ProtectedRoute>} />
      <Route path="/incentives/schemes/:id" element={<ProtectedRoute><IncentiveSchemeEditorPage /></ProtectedRoute>} />
      <Route path="/incentives/new" element={<ProtectedRoute><IncentiveRunPage /></ProtectedRoute>} />
      <Route path="/incentives/:id" element={<ProtectedRoute><IncentiveRunPage /></ProtectedRoute>} />

      {/* Count Mode (Phase A — tablet-optimised counting ONLY) */}
      <Route path="/count" element={<CountProtectedRoute><CountSessionPickerPage /></CountProtectedRoute>} />
      <Route path="/count/sessions" element={<CountProtectedRoute><CountSessionPickerPage /></CountProtectedRoute>} />
      <Route path="/count/sessions/:id" element={<CountProtectedRoute><CountSessionPage /></CountProtectedRoute>} />
      <Route path="/count/sessions/:id/sync" element={<CountProtectedRoute><CountSyncPage /></CountProtectedRoute>} />
      <Route path="/count/sessions/:id/reconcile" element={<CountProtectedRoute><ReconciliationPage /></CountProtectedRoute>} />
      <Route path="/count/sessions/:id/observations" element={<CountProtectedRoute><CountObservationsPage /></CountProtectedRoute>} />
      <Route path="/count/observations" element={<CountProtectedRoute><CountObservationsPage /></CountProtectedRoute>} />
      <Route path="/count/transfer" element={<CountProtectedRoute><CountTransferPage /></CountProtectedRoute>} />

      {/* Exception Queue (Phase B — Admin review) */}
      <Route path="/stocktaking/exception-queue" element={<ProtectedRoute><ExceptionQueuePage /></ProtectedRoute>} />
      <Route path="/apps" element={<ProtectedRoute><AppsPage /></ProtectedRoute>} />

      <Route path="*" element={<ImpersonationAwareFallback />} />
    </Routes>
    </CountPWAGuard>
  );
}

// Fallback: if impersonating a CLIENT, redirect to client dashboard (ring-fence).
// Auditor-view and all other cases send the user to /home.
function ImpersonationAwareFallback() {
  const { isClientView } = useImpersonation();
  return <Navigate to={isClientView ? '/client/dashboard' : '/home'} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary fallbackTitle="Something went wrong">
        <AuthProvider>
          <SettingsProvider>
            <OfflineProvider>
              <ImpersonationProvider>
                <ImpersonationBanner />
                <MaintenanceBanner />
                <IdleTimeout />
                <AppRoutes />
                <PerformanceBadge />
                <UATLauncher />
              </ImpersonationProvider>
            </OfflineProvider>
          </SettingsProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
